<script setup lang="ts">
import stores from '@jaaluu/stores';
import NumberFlow from '@number-flow/vue';
import { t } from '@shared/i18n';
import sharedStores from '@shared/shared-stores';
import { computed, onMounted, watch } from 'vue';

const { addConfetti } = sharedStores.animate.funcs;
const { showMilestoneAnimation, openSynapsisScoreDialog } = stores.favorites.state;
const { langForeign } = stores.language.state;

const country = computed(() => (langForeign.value ? t('countries.' + langForeign.value) : ''));

const baseUrl = (import.meta as any).env.VITE_JAALUU_API_URL;
const milestoneSound = new Audio(`${baseUrl}/v1/file/sounds/success_long.mp3`);

onMounted(() => {
  if (showMilestoneAnimation.value.show) {
    celebrate();
  }
});

const celebrate = () => {
  addConfetti();
  milestoneSound.play().catch((err) => console.error('Error playing milestone sound:', err));
};

watch(
  () => showMilestoneAnimation.value.show,
  (show) => {
    if (show) celebrate();
  }
);
</script>

<template>
  <v-snackbar v-model="showMilestoneAnimation.show" multi-line color="white" timeout="8000" location="top">
    <div class="title d-flex justify-center">{{ t('general.well_done') }}</div>

    <div class="milestone-stars-ring mx-auto my-3">
      <v-icon class="orbit-star s1" icon="mdi-star" color="light-blue-lighten-3" size="16" />
      <v-icon class="orbit-star s2" icon="mdi-star" color="cyan-lighten-2" size="14" />
      <v-icon class="orbit-star s3" icon="mdi-star" color="light-blue-lighten-3" size="16" />
      <v-icon class="orbit-star s4" icon="mdi-star" color="cyan-lighten-2" size="14" />
      <v-icon class="orbit-star s5" icon="mdi-star" color="light-blue-lighten-3" size="16" />
      <v-icon class="orbit-star s6" icon="mdi-star" color="cyan-lighten-2" size="14" />
      <div class="trophy-wrapper">
        <v-icon class="trophy-center mt-5" icon="mdi-trophy" color="light-blue-lighten-2" size="40" />
      </div>
    </div>

    <div class="d-flex flex-column align-center">
      <div class="">
        <span class="milestone-label">{{ t('general.synapsis_milestone') }}</span>
      </div>
      <div class="d-flex align-center mt-3" v-if="showMilestoneAnimation.show">
        <v-chip variant="tonal" color="light-blue-lighten-2" size="large" class="score-chip">
          <v-icon size="20" color="light-blue-lighten-2" icon="mdi-brain" class="mr-2" />
          <NumberFlow :value="showMilestoneAnimation.score" class="title" />
        </v-chip>
      </div>
      <div class="mt-3 text-center fun-fact-hint milestone-badge cursor-pointer" @click="() => { openSynapsisScoreDialog = true; showMilestoneAnimation.show = false; }">{{ t('general.fun_fact_unlocked', { country }) }} 😄</div>
    </div>
  </v-snackbar>
</template>

<style scoped lang="scss">
.milestone-stars-ring {
  position: relative;
  width: 90px;
  height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.trophy-wrapper {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.trophy-center {
  animation: trophy-pop 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards,
    trophy-glow 1.5s ease-in-out 0.5s infinite;
}

@keyframes trophy-pop {
  0%   { transform: scale(0); opacity: 0; }
  70%  { transform: scale(1.25); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}

@keyframes trophy-glow {
  0%, 100% { filter: drop-shadow(0 0 4px rgba(100, 200, 255, 0.4)); }
  50%       { filter: drop-shadow(0 0 16px rgba(100, 220, 255, 1)); }
}

.orbit-star {
  position: absolute;
  top: 50%;
  left: 50%;
  transform-origin: 0 0;
  animation: orbit 2.4s linear infinite;
}

.s1 { --angle: 0deg;   animation-delay: 0s; }
.s2 { --angle: 60deg;  animation-delay: -0.4s; }
.s3 { --angle: 120deg; animation-delay: -0.8s; }
.s4 { --angle: 180deg; animation-delay: -1.2s; }
.s5 { --angle: 240deg; animation-delay: -1.6s; }
.s6 { --angle: 300deg; animation-delay: -2.0s; }

@keyframes orbit {
  from { transform: rotate(var(--angle)) translateX(40px) rotate(calc(-1 * var(--angle))); }
  to   { transform: rotate(calc(var(--angle) + 360deg)) translateX(40px) rotate(calc(-1 * (var(--angle) + 360deg))); }
}

.milestone-badge {
  background: linear-gradient(90deg, #00aeff, #a0e1ec);
  border-radius: 12px;
  padding: 5px 16px;
}

.milestone-label {
  font-size: 0.85rem;
  letter-spacing: 0.03em;
}

.score-chip {
  animation: jump 0.6s ease-in-out infinite;
}

@keyframes jump {
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-12px); }
}
</style>
